import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "group/badge inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent whitespace-nowrap outline-none ring-ring/15 transition-colors focus-visible:border-ring focus-visible:ring-2 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-2 aria-invalid:ring-destructive/15 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary: "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        neutral:
          "border-badge-neutral-border bg-badge-neutral-background text-badge-neutral-foreground [a]:hover:bg-badge-neutral-background/80",
        success:
          "border-badge-success-border bg-badge-success-background text-badge-success-foreground [a]:hover:bg-badge-success-background/80",
        warning:
          "border-badge-warning-border bg-badge-warning-background text-badge-warning-foreground [a]:hover:bg-badge-warning-background/80",
        info: "border-badge-information-border bg-badge-information-background text-badge-information-foreground [a]:hover:bg-badge-information-background/80",
        sp: "bg-campaign-type-sp px-2.5 font-normal text-white [a]:hover:bg-campaign-type-sp/85",
        sb: "bg-campaign-type-sb px-2.5 font-normal text-white [a]:hover:bg-campaign-type-sb/85",
        sd: "bg-campaign-type-sd px-2.5 font-normal text-white [a]:hover:bg-campaign-type-sd/85",
        inverse: "bg-foreground text-background [a]:hover:bg-foreground/85",
        destructive:
          "border-badge-destructive-border bg-badge-destructive-background text-badge-destructive-foreground [a]:hover:bg-badge-destructive-background/80",
        outline: "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        ghost: "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        compact: "h-5 px-1.5 text-xs leading-4 font-semibold",
        default: "h-6 px-2 text-xs leading-4 font-medium",
        emphasis: "h-7 px-2.5 text-xs leading-4 font-medium",
      },
    },
    defaultVariants: {
      size: "default",
      variant: "default",
    },
  },
);

function Badge({
  className,
  size = "default",
  variant = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ size, variant }), className),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      size,
      variant,
    },
  });
}

export { Badge, badgeVariants };
